import type {
  MedicationProductType,
  VaccineType,
} from "./MedicationsVaccinesContext";

export interface MedicationProductOption {
  id: MedicationProductType;
  labelKey: string;
  emoji: string;
}

const MED_LABEL_KEY = (id: MedicationProductType): string =>
  `med_history.medical.medications_vaccines.medication_products.${id}`;

export const MEDICATION_PRODUCT_OPTIONS: MedicationProductOption[] = [
  { id: "nose_drops", labelKey: MED_LABEL_KEY("nose_drops"), emoji: "💧" },
  {
    id: "cold_medicine",
    labelKey: MED_LABEL_KEY("cold_medicine"),
    emoji: "🤧",
  },
  { id: "diuretics", labelKey: MED_LABEL_KEY("diuretics"), emoji: "🚽" },
  { id: "pain_relief", labelKey: MED_LABEL_KEY("pain_relief"), emoji: "💊" },
  { id: "relaxants", labelKey: MED_LABEL_KEY("relaxants"), emoji: "🧘" },
  {
    id: "sleeping_pills",
    labelKey: MED_LABEL_KEY("sleeping_pills"),
    emoji: "😴",
  },
  {
    id: "allergy_medication",
    labelKey: MED_LABEL_KEY("allergy_medication"),
    emoji: "🌸",
  },
  {
    id: "nicotine_subs",
    labelKey: MED_LABEL_KEY("nicotine_subs"),
    emoji: "🚭",
  },
  { id: "other", labelKey: MED_LABEL_KEY("other"), emoji: "✨" },
  { id: "none", labelKey: MED_LABEL_KEY("none"), emoji: "🚫" },
];

/** English labels historically persisted as medication names. */
export const MEDICATION_PRODUCT_LABELS: Record<MedicationProductType, string> =
  {
    nose_drops: "Nose drops",
    cold_medicine: "Cold medicine",
    diuretics: "Diuretics",
    pain_relief: "Pain or inflammation relief",
    relaxants: "Relaxants",
    sleeping_pills: "Sleeping pills",
    allergy_medication: "Allergy medication",
    nicotine_subs: "Nicotine subs & anti smoking products",
    other: "Other",
    none: "None",
  };

const LEGACY_FRENCH_MEDICATION_PRODUCT_LABELS: Record<
  MedicationProductType,
  string
> = {
  nose_drops: "Gouttes nasales",
  cold_medicine: "Médicament contre le rhume",
  diuretics: "Diurétiques",
  pain_relief: "Antidouleur ou anti-inflammatoire",
  relaxants: "Relaxants",
  sleeping_pills: "Somnifères",
  allergy_medication: "Médicament contre les allergies",
  nicotine_subs: "Substituts nicotiniques & produits anti-tabac",
  other: "Autre",
  none: "Aucun",
};

export const MEDICATION_LABEL_TO_ID: Record<string, MedicationProductType> =
  Object.entries(MEDICATION_PRODUCT_LABELS).reduce(
    (acc, [id, label]) => {
      for (const translatedLabel of [
        label,
        LEGACY_FRENCH_MEDICATION_PRODUCT_LABELS[
          id as MedicationProductType
        ],
      ]) {
        const normalized = translatedLabel.toLowerCase();
        acc[normalized] = id as MedicationProductType;
        acc[normalized.replace(/[^a-z0-9]/g, "")] =
          id as MedicationProductType;
      }
      return acc;
    },
    {} as Record<string, MedicationProductType>,
  );

export function getMedicationProductIdFromLabel(
  label: string,
): MedicationProductType | undefined {
  const normalized = label.toLowerCase().trim();
  return (
    MEDICATION_LABEL_TO_ID[normalized] ??
    MEDICATION_LABEL_TO_ID[normalized.replace(/[^a-z0-9]/g, "")]
  );
}

export function getMedicationProductLabelKey(
  id: MedicationProductType,
): string {
  return (
    MEDICATION_PRODUCT_OPTIONS.find((o) => o.id === id)?.labelKey ??
    MED_LABEL_KEY("other")
  );
}

export interface VaccineOption {
  id: VaccineType | "none";
  labelKey: string;
  emoji: string;
}

const VAX_LABEL_KEY = (id: VaccineType | "none"): string =>
  `med_history.medical.medications_vaccines.vaccine_options.${id}`;

export const VACCINE_OPTIONS: VaccineOption[] = [
  { id: "hpv", labelKey: VAX_LABEL_KEY("hpv"), emoji: "💉" },
  { id: "covid19", labelKey: VAX_LABEL_KEY("covid19"), emoji: "🦠" },
  { id: "chicken_pox", labelKey: VAX_LABEL_KEY("chicken_pox"), emoji: "🐔" },
  { id: "tetanus", labelKey: VAX_LABEL_KEY("tetanus"), emoji: "🧫" },
  { id: "polio", labelKey: VAX_LABEL_KEY("polio"), emoji: "🦵" },
  { id: "influenza", labelKey: VAX_LABEL_KEY("influenza"), emoji: "🤧" },
  { id: "mmr", labelKey: VAX_LABEL_KEY("mmr"), emoji: "👶" },
  { id: "hepatitis_a", labelKey: VAX_LABEL_KEY("hepatitis_a"), emoji: "🅰️" },
  { id: "hepatitis_b", labelKey: VAX_LABEL_KEY("hepatitis_b"), emoji: "🅱️" },
  { id: "other", labelKey: VAX_LABEL_KEY("other"), emoji: "✨" },
  { id: "none", labelKey: VAX_LABEL_KEY("none"), emoji: "🚫" },
];

export function getVaccineLabelKey(id: VaccineType | "none"): string {
  return (
    VACCINE_OPTIONS.find((o) => o.id === id)?.labelKey ?? VAX_LABEL_KEY("other")
  );
}

export const VACCINE_TYPE_VALUES = VACCINE_OPTIONS.map((option) => option.id);
